import { LinkNode, SerializedAutoLinkNode, SerializedLinkNode } from '@lexical/link';
import { ListItemNode, ListNode, SerializedListItemNode, SerializedListNode } from '@lexical/list';
import {
  $getRoot,
  createEditor,
  EditorState,
  ElementFormatType,
  ParagraphNode,
  SerializedEditorState,
  SerializedElementNode,
  SerializedLexicalNode,
  SerializedLineBreakNode,
  SerializedParagraphNode,
  SerializedTabNode,
  TextNode,
} from 'lexical';
import { RichText, RichTextNode, TextDecorations } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import type {
  StreamingRichText,
  StreamingRichTextNode,
  StreamingRichTextElement,
} from '@mutiny-pkg/champaign-schemas/src/schemas/richText/streaming';

import { MentionNode, SerializedMentionNode } from './MentionsPlugin/MentionNode';
import { ExtendedTextNode, SerializedExtendedTextNode } from './ExtendedTextPlugin/ExtendedTextNode';
import {
  CustomParagraphNode,
  SerializedCustomParagraphNode,
} from '@mutiny-pkg/bin-ui/src/richtext/CustomParagraphNode/CustomParagraphNode';
import { LookupNode, SerializedLookupNode } from './LookupPlugin/LookupNode';
import {
  CustomLinkNode,
  SerializedCustomLinkNode,
} from '@mutiny-pkg/bin-ui/src/richtext/CustomLinkNode/CustomLinkNode';

/**
 * Type guard to check if a streaming node has all required fields for a complete RichTextNode
 */
function isCompleteRichTextNode(node: StreamingRichTextNode | RichTextNode): node is RichTextNode {
  if (!node || typeof node !== 'object' || !('type' in node)) {
    return false;
  }

  switch (node.type) {
    case 'text':
      return 'content' in node && typeof node.content === 'string' && 'decorations' in node;
    case 'variable':
      return 'columnId' in node && typeof node.columnId === 'string';
    case 'lookup':
      return 'linkColumnId' in node && 'refLookupColumnId' in node;
    case 'link':
      return 'url' in node && typeof node.url === 'string' && 'children' in node && Array.isArray(node.children);
    case 'paragraph':
    case 'listitem':
      return 'children' in node && Array.isArray(node.children);
    case 'list':
      return 'listType' in node && 'children' in node && Array.isArray(node.children);
    case 'linebreak':
    case 'tab':
      return true;
    default:
      return false;
  }
}

/**
 * Type guard to check if a streaming element is a complete RichTextElement
 */
function isCompleteRichTextElement(
  element: StreamingRichTextElement | RichText['children'][number],
): element is RichText['children'][number] {
  if (!element || typeof element !== 'object' || !('type' in element)) {
    return false;
  }

  if (element.type === 'paragraph' || element.type === 'list') {
    return 'children' in element && Array.isArray(element.children);
  }

  return false;
}

/**
 * Normalizes a potentially streaming node to have safe defaults for processing.
 * Returns a node that can be safely processed by Lexical conversion functions.
 */
function normalizeStreamingNode(node: StreamingRichTextNode | RichTextNode): RichTextNode {
  if (!node || typeof node !== 'object' || !('type' in node)) {
    return { type: 'text', content: '', decorations: {} };
  }

  switch (node.type) {
    case 'text':
      return {
        type: 'text',
        content: ('content' in node && node.content) || '',
        decorations: ('decorations' in node && node.decorations) || {},
      };
    case 'variable':
      return {
        type: 'variable',
        columnId: ('columnId' in node && node.columnId) || '',
        decorations: ('decorations' in node && node.decorations) || {},
      };
    case 'lookup':
      return {
        type: 'lookup',
        linkColumnId: ('linkColumnId' in node && node.linkColumnId) || '',
        refLookupColumnId: ('refLookupColumnId' in node && node.refLookupColumnId) || '',
        decorations: ('decorations' in node && node.decorations) || {},
      };
    case 'link':
      return {
        type: 'link',
        url: ('url' in node && node.url) || '',
        children: 'children' in node && Array.isArray(node.children) ? node.children.map(normalizeStreamingNode) : [],
      };
    case 'paragraph':
      return {
        type: 'paragraph',
        children: 'children' in node && Array.isArray(node.children) ? node.children.map(normalizeStreamingNode) : [],
      };
    case 'listitem':
      return {
        type: 'listitem',
        children: 'children' in node && Array.isArray(node.children) ? node.children.map(normalizeStreamingNode) : [],
      };
    case 'list':
      return {
        type: 'list',
        listType: ('listType' in node && node.listType) || 'unordered',
        children: 'children' in node && Array.isArray(node.children) ? node.children.map(normalizeStreamingNode) : [],
      };
    case 'linebreak':
      return { type: 'linebreak' };
    case 'tab':
      return { type: 'tab' };
    default:
      return { type: 'text', content: '', decorations: {} };
  }
}

/**
 * Normalizes a potentially streaming element to have safe defaults for processing.
 */
function normalizeStreamingElement(
  element: StreamingRichTextElement | RichText['children'][number],
): RichText['children'][number] {
  if (!element || typeof element !== 'object' || !('type' in element)) {
    return { type: 'paragraph', children: [] };
  }

  if (element.type === 'paragraph') {
    return {
      type: 'paragraph',
      children:
        'children' in element && Array.isArray(element.children) ? element.children.map(normalizeStreamingNode) : [],
    };
  }

  if (element.type === 'list') {
    return {
      type: 'list',
      listType: ('listType' in element && element.listType) || 'unordered',
      children:
        'children' in element && Array.isArray(element.children) ? element.children.map(normalizeStreamingNode) : [],
    };
  }

  return { type: 'paragraph', children: [] };
}

/**
 * Normalizes a potentially streaming RichText object to a complete RichText object.
 * This is the main entry point for components to normalize streaming data before
 * passing it to utility functions that expect complete RichText.
 */
export function normalizeStreamingRichText(value: StreamingRichText | RichText | string): RichText | string;
export function normalizeStreamingRichText(value: undefined | null): undefined;
export function normalizeStreamingRichText(
  value: StreamingRichText | RichText | string | undefined | null,
): RichText | string | undefined;
export function normalizeStreamingRichText(
  value: StreamingRichText | RichText | string | undefined | null,
): RichText | string | undefined {
  if (value == null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object') {
    return { type: 'root', version: 1, children: [] };
  }

  const children =
    'children' in value && Array.isArray(value.children) ? value.children.map(normalizeStreamingElement) : [];

  return {
    type: 'root',
    version: 1,
    children,
    format: value.format,
  };
}

// Re-export type guards for use in components
export { isCompleteRichTextNode, isCompleteRichTextElement };

function isNodeType<T extends SerializedLexicalNode>(node: SerializedLexicalNode, type: string): node is T {
  return node.type === type;
}

const BOLD_MASK = 1;
const ITALIC_MASK = 2;
const STRIKETHROUGH_MASK = 4;
const UNDERLINE_MASK = 8;

function generateRichTextNodeFromLexicalNode(lexicalNode: SerializedLexicalNode): RichTextNode {
  if (
    isNodeType<SerializedLinkNode>(lexicalNode, 'link') ||
    isNodeType<SerializedAutoLinkNode>(lexicalNode, 'autolink') ||
    isNodeType<SerializedCustomLinkNode>(lexicalNode, 'custom-link')
  ) {
    return {
      type: 'link',
      url: lexicalNode.url,
      children: lexicalNode.children?.map(generateRichTextNodeFromLexicalNode),
    };
  }

  if (isNodeType<SerializedListNode>(lexicalNode, 'list')) {
    return {
      type: 'list',
      listType: lexicalNode.listType === 'number' ? 'ordered' : 'unordered',
      children: lexicalNode.children?.map(generateRichTextNodeFromLexicalNode),
    };
  }

  if (isNodeType<SerializedListItemNode>(lexicalNode, 'listitem')) {
    return {
      type: 'listitem',
      children: lexicalNode.children?.map(generateRichTextNodeFromLexicalNode),
    };
  }

  const format = 'format' in lexicalNode ? (lexicalNode.format as number) : 0;
  // TODO: Give this a type so we don't have to set highlight to blank string
  const decorations = {
    bold: Boolean(format & BOLD_MASK),
    italic: Boolean(format & ITALIC_MASK),
    strikethrough: Boolean(format & STRIKETHROUGH_MASK),
    underline: Boolean(format & UNDERLINE_MASK),
    highlight: '',
    color: '',
  };

  if (isNodeType<SerializedMentionNode>(lexicalNode, 'mention')) {
    if (lexicalNode.highlightColor) decorations.highlight = lexicalNode.highlightColor;
    if (lexicalNode.fontColor) decorations.color = lexicalNode.fontColor;
    return {
      type: 'variable',
      columnId: lexicalNode.value,
      decorations,
    };
  }
  if (isNodeType<SerializedLookupNode>(lexicalNode, 'lookup')) {
    if (lexicalNode.highlightColor) decorations.highlight = lexicalNode.highlightColor;
    if (lexicalNode.fontColor) decorations.color = lexicalNode.fontColor;
    return {
      type: 'lookup',
      linkColumnId: lexicalNode.linkColumnId,
      refLookupColumnId: lexicalNode.refLookupColumnId,
      decorations,
    };
  }

  if (isNodeType<SerializedExtendedTextNode>(lexicalNode, 'extended-text')) {
    if (lexicalNode.highlightColor) decorations.highlight = lexicalNode.highlightColor;
    if (lexicalNode.fontColor) decorations.color = lexicalNode.fontColor;
    return {
      type: 'text',
      decorations,
      content: lexicalNode.text,
    };
  }

  if (
    isNodeType<SerializedElementNode>(lexicalNode, 'element') ||
    isNodeType<SerializedParagraphNode>(lexicalNode, 'paragraph') ||
    isNodeType<SerializedCustomParagraphNode>(lexicalNode, 'custom-paragraph')
  ) {
    return {
      type: 'paragraph',
      children: lexicalNode.children?.map(generateRichTextNodeFromLexicalNode),
    };
  }

  if (isNodeType<SerializedLineBreakNode>(lexicalNode, 'linebreak')) {
    return {
      type: 'linebreak',
    };
  }
  if (isNodeType<SerializedTabNode>(lexicalNode, 'tab')) {
    return {
      type: 'tab',
    };
  }

  throw new Error(`Unknown node type: ${(lexicalNode as SerializedLexicalNode).type}`);
}

export function toRichText(editorState: EditorState): RichText {
  const { root } = editorState.toJSON();
  const children = root.children.map(generateRichTextNodeFromLexicalNode) as RichText['children'];
  return {
    type: 'root',
    version: 1,
    children,
    format: root.format,
  };
}

const parseDecorations = (decorations?: TextDecorations) => {
  if (!decorations) {
    return 0;
  }

  return (
    (decorations?.bold ? BOLD_MASK : 0) +
    (decorations?.italic ? ITALIC_MASK : 0) +
    (decorations?.strikethrough ? STRIKETHROUGH_MASK : 0) +
    (decorations?.underline ? UNDERLINE_MASK : 0)
  );
};

type SerializedNode =
  | SerializedExtendedTextNode
  | SerializedLinkNode
  | SerializedMentionNode
  | SerializedLookupNode
  | SerializedAutoLinkNode
  | SerializedListNode
  | SerializedListItemNode
  | SerializedParagraphNode
  | SerializedLineBreakNode
  | SerializedTabNode;

function generateLexicalNodeFromRichTextNode(richtextNode: RichTextNode, index?: number): SerializedNode {
  if (richtextNode.type === 'text') {
    return {
      type: 'extended-text',
      mode: 'normal' as const,
      format: parseDecorations(richtextNode.decorations),
      text: richtextNode.content,
      highlightColor: richtextNode.decorations.highlight,
      fontColor: richtextNode.decorations.color,
      // Set resolved colors immediately so they're available for rendering
      resolvedHighlightColor: richtextNode.decorations.highlight,
      resolvedFontColor: richtextNode.decorations.color,
      detail: 0,
      style: '',
      version: 1,
    };
  }
  if (richtextNode.type === 'link') {
    return {
      type: 'custom-link',
      url: richtextNode.url,
      children: richtextNode.children?.map(generateLexicalNodeFromRichTextNode),
      direction: null,
      format: '',
      indent: 0,
      version: 1,
    };
  }
  if (richtextNode.type === 'variable') {
    return {
      type: 'mention',
      value: richtextNode.columnId,
      version: 1,
      format: parseDecorations(richtextNode.decorations),
      highlightColor: richtextNode.decorations?.highlight,
      fontColor: richtextNode.decorations?.color,
      style: '',
    };
  }
  if (richtextNode.type === 'lookup') {
    return {
      type: 'lookup',
      linkColumnId: richtextNode.linkColumnId,
      refLookupColumnId: richtextNode.refLookupColumnId,
      version: 1,
      format: parseDecorations(richtextNode.decorations),
      highlightColor: richtextNode.decorations?.highlight,
      fontColor: richtextNode.decorations?.color,
      style: '',
    };
  }

  if (richtextNode.type === 'list') {
    return {
      type: 'list',
      listType: richtextNode.listType === 'ordered' ? 'number' : 'bullet',
      children: richtextNode.children?.map((value, childIndex) =>
        generateLexicalNodeFromRichTextNode(value, childIndex),
      ),
      indent: 0,
      version: 1,
    };
  }

  if (richtextNode.type === 'listitem') {
    return {
      type: 'listitem',
      value: (index ?? 0) + 1,
      children: richtextNode.children?.map(generateLexicalNodeFromRichTextNode),
      indent: 0,
      version: 1,
    };
  }

  if (richtextNode.type === 'paragraph') {
    return {
      type: 'custom-paragraph',
      mode: 'normal',
      children: richtextNode.children?.map(generateLexicalNodeFromRichTextNode),
      indent: 0,
      version: 1,
    };
  }

  if (richtextNode.type === 'linebreak') {
    return {
      type: 'linebreak',
      version: 1,
    };
  }
  if (richtextNode.type === 'tab') {
    return {
      type: 'tab',
      version: 1,
    };
  }

  throw new Error('Unknown node type');
}

function generateLexicalTopLevelNodeFromRichTextNode(
  richtextNode: RichTextNode,
  index?: number,
  format?: ElementFormatType,
): SerializedNode {
  if (richtextNode.type === 'paragraph') {
    return {
      type: 'custom-paragraph',
      children: richtextNode.children?.map(generateLexicalNodeFromRichTextNode),
      format: format ?? '',
      version: 1,
      indent: 0,
    };
  }

  return generateLexicalNodeFromRichTextNode(richtextNode, index);
}

function isEmptyRichTextNode(node: RichTextNode) {
  if (node.type === 'text') {
    return !node.content.trim();
  }

  if (node.type === 'variable') {
    return !node.columnId;
  }

  if ('children' in node) {
    return node.children.every(isEmptyRichTextNode);
  }

  return true;
}

export function isEmptyRichText(text: RichText | string): boolean {
  if (typeof text === 'string') {
    return !text.trim();
  }

  if (text.children.length === 0) {
    return true;
  }

  return text.children.every(isEmptyRichTextNode);
}

function generateChildren(value: string) {
  return [
    {
      children: [
        generateLexicalNodeFromRichTextNode({
          type: 'text',
          content: value ?? '',
          decorations: {},
        }),
      ],
      type: 'paragraph',
      indent: 0,
      version: 1,
    },
  ];
}

export function toEditorState(value: RichText | string): SerializedEditorState {
  let children;
  let format: ElementFormatType = '';

  if (typeof value === 'string') {
    children = generateChildren(value);
  } else if (typeof value === 'object' && 'children' in value && Array.isArray(value.children)) {
    // Handle empty children array - Lexical requires at least one paragraph node
    if (value.children.length === 0) {
      children = generateChildren('');
    } else {
      children = value.children.map((node, idx) =>
        generateLexicalTopLevelNodeFromRichTextNode(node, idx, value.format),
      );
    }
    format = value.format ?? '';
  } else {
    children = generateChildren('');
  }

  return {
    root: {
      children,
      direction: 'ltr',
      format,
      indent: 0,
      type: 'root',
      version: 1,
    },
  };
}

export function isRichTextEqual(
  a: RichText | string | undefined | null,
  b: RichText | string | undefined | null,
): boolean {
  // Handle undefined/null cases
  if (a == null && b == null) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a === b;
  }

  // type mismatch
  if (typeof a === 'string' || typeof b === 'string') {
    return false;
  }
  // format is present on one of the objects and is not the same as the other
  // this catches cases where one is undefined and the other is '' (an empty string)
  if (a.format !== b.format && (a.format || b.format)) {
    return false;
  }

  return isRichTextNodeListEqual(a.children, b.children);
}

export function normalizeTextNodes(nodes: RichTextNode[]) {
  const normalizedNodes: RichTextNode[] = [];

  nodes.forEach((currentNode: RichTextNode) => {
    const previousNode = normalizedNodes[normalizedNodes.length - 1];
    if (
      normalizeTextNodes.length &&
      previousNode?.type === 'text' &&
      currentNode.type === 'text' &&
      isDecorationEqual(currentNode.decorations, previousNode.decorations)
    ) {
      // Combine content and create new node.
      const combinedContent = previousNode.content + currentNode.content;
      const clonedNodeWithCombinedContent: RichTextNode = {
        type: 'text',
        decorations: { ...previousNode.decorations },
        content: combinedContent,
      };
      // Replace previous node with cloned node.
      normalizedNodes.pop();
      normalizedNodes.push(clonedNodeWithCombinedContent);
    } else {
      normalizedNodes.push(currentNode);
    }
  });

  return normalizedNodes;
}

function isRichTextNodeListEqual(a: Array<RichTextNode>, b: Array<RichTextNode>) {
  const normalizedA = normalizeTextNodes(a);
  const normalizedB = normalizeTextNodes(b);
  if (normalizedA.length !== normalizedB.length) {
    return false;
  }
  return normalizedA.every((node, index) => {
    const otherNode = normalizedB[index];
    if (!otherNode) {
      return false;
    }
    return isRichTextNodeEqual(node, otherNode);
  });
}

function isRichTextNodeEqual(a: RichTextNode, b: RichTextNode): boolean {
  if (a.type !== b.type) return false;

  if (a.type === 'linebreak' && b.type === 'linebreak') {
    return true;
  }
  if (a.type === 'tab' && b.type === 'tab') {
    return true;
  }
  if (a.type === 'variable' && b.type === 'variable') {
    return a.columnId === b.columnId && isDecorationEqual(a.decorations || {}, b.decorations || {});
  }

  if (a.type === 'text' && b.type === 'text') {
    return a.content === b.content && isDecorationEqual(a.decorations, b.decorations);
  }
  if (a.type === 'link' && b.type === 'link') {
    return a.url === b.url && isRichTextNodeListEqual(a.children, b.children);
  }

  if (a.type === 'paragraph' && b.type === 'paragraph') {
    return isRichTextNodeListEqual(a.children, b.children);
  }

  if (a.type === 'listitem' && b.type === 'listitem') {
    return isRichTextNodeListEqual(a.children, b.children);
  }

  if (a.type === 'list' && b.type === 'list') {
    return a.listType === b.listType && isRichTextNodeListEqual(a.children, b.children);
  }

  throw new Error(`Unknown node type: ${a.type}`);
}

function isDecorationEqual(a: TextDecorations, b: TextDecorations) {
  return (
    isDecorationPropertyEqual(a.bold, b.bold) &&
    isDecorationPropertyEqual(a.italic, b.italic) &&
    isDecorationPropertyEqual(a.strikethrough, b.strikethrough) &&
    isDecorationPropertyEqual(a.underline, b.underline) &&
    (a.highlight === b.highlight ||
      (a.highlight === undefined && b.highlight === '') ||
      (a.highlight === '' && b.highlight === undefined)) &&
    (a.color === b.color || (a.color === undefined && b.color === '') || (a.color === '' && b.color === undefined))
  );
}
function isDecorationPropertyEqual(a: boolean | undefined, b: boolean | undefined) {
  return a === b || (a === undefined && b === false) || (a === false && b === undefined);
}
export const HEADLESS_LEXICAL_CONFIG = {
  namespace: 'MutinyRichInput',
  nodes: [
    MentionNode,
    LookupNode,
    ListItemNode,
    ListNode,
    CustomLinkNode,
    {
      replace: LinkNode,
      with: (node: LinkNode) => {
        return new CustomLinkNode(node.getURL());
      },
      withKlass: CustomLinkNode,
    },
    ExtendedTextNode,
    CustomParagraphNode,
    {
      replace: TextNode,
      with: (node: TextNode) => {
        return new ExtendedTextNode(node.getTextContent());
      },
      withKlass: ExtendedTextNode,
    },
    {
      replace: ParagraphNode,
      with: () => {
        return new CustomParagraphNode();
      },
      withKlass: CustomParagraphNode,
    },
  ],
  onError(error: Error) {
    // TODO: we should add some handling here
    throw error;
  },
};

export function renderRichTextToPlainString(richText: RichText | StreamingRichText | string): string;
export function renderRichTextToPlainString(richText: undefined | null): string;
export function renderRichTextToPlainString(richText: RichText | StreamingRichText | string | undefined | null): string;
export function renderRichTextToPlainString(
  richText: RichText | StreamingRichText | string | undefined | null,
): string {
  if (richText == null) {
    return '';
  }

  // Normalize streaming RichText to complete RichText
  const normalized = normalizeStreamingRichText(richText);
  if (normalized === undefined) {
    return '';
  }

  const editor = createEditor({
    ...HEADLESS_LEXICAL_CONFIG,
  });
  const editorState = editor.parseEditorState(toEditorState(normalized));
  editor.setEditorState(editorState);

  let result = '';
  let error = null;
  // NOTE: the "callback" seems to be executed immediately so we can just lift the values out of the closure
  editor.read(() => {
    try {
      result = $getRoot().getTextContent();
    } catch (e) {
      error = e;
    }
  });

  if (error) {
    throw error;
  }

  return result;
}

export function checkEmptyRichTextVariables<T extends RichText | RichTextNode>(
  node: T,
  variables: Partial<Record<string, string>>,
): string[] {
  return checkEmpty<T>(node, variables, []);
}

function checkEmpty<T extends RichText | RichTextNode>(
  node: T,
  variables: Partial<Record<string, string>>,
  emptyVars: string[],
): string[] {
  if (!node || typeof node !== 'object' || !('type' in node)) {
    return emptyVars;
  }

  if (node.type === 'variable') {
    if (!variables[node.columnId] || variables[node.columnId] === '') {
      return [...emptyVars, node.columnId];
    }
  }

  if ('children' in node && Array.isArray(node.children)) {
    return node.children.map((child) => checkEmpty(child, variables, emptyVars)).flat();
  }

  return emptyVars;
}

export function hydrateRichTextVariables<T extends RichText | RichTextNode>(
  node: T,
  variables: Partial<Record<string, string>>,
  showMentionNodeWhenValueMissing?: boolean,
): T;
export function hydrateRichTextVariables(
  node: undefined,
  variables: Partial<Record<string, string>>,
  showMentionNodeWhenValueMissing?: boolean,
): undefined;
export function hydrateRichTextVariables<T extends RichText | RichTextNode>(
  node: T | undefined,
  variables: Partial<Record<string, string>>,
  showMentionNodeWhenValueMissing?: boolean,
): T | undefined;
export function hydrateRichTextVariables<T extends RichText | RichTextNode>(
  node: T | undefined,
  variables: Partial<Record<string, string>>,
  showMentionNodeWhenValueMissing: boolean = false,
): T | undefined {
  if (node === undefined) {
    return undefined;
  }
  // If node is a string, return it as-is
  if (typeof node === 'string') {
    return node;
  }

  // If node is not an object or doesn't have a type property, return as-is
  if (!node || typeof node !== 'object' || !('type' in node)) {
    return node;
  }

  if (node.type === 'variable') {
    const value = variables[node.columnId];
    if (value || !showMentionNodeWhenValueMissing) {
      return {
        type: 'text',
        content: value || '',
        decorations: node.decorations || {},
      } as T;
    }
    return node;
  }
  // Handle root node separately
  if (node.type === 'root') {
    return {
      ...node,
      children: node.children.map(
        (child) => hydrateRichTextVariables(child, variables, showMentionNodeWhenValueMissing) as RichTextNode,
      ),
    };
  }
  if ('children' in node && Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map(
        (child) => hydrateRichTextVariables(child, variables, showMentionNodeWhenValueMissing) as RichTextNode,
      ),
    };
  }
  return node;
}

/**
 * Recursively strip all link nodes from RichText AST, converting link children to plain nodes.
 * Returns undefined if input is undefined/null (can occur during streaming).
 *
 * @param richText The RichText structure to process (may be undefined during streaming)
 * @returns The same structure with all links removed, or undefined if input was undefined
 */
export function stripLinksFromRichText(richText: RichText | undefined | null): RichText | undefined {
  // Guard for undefined/null during streaming
  if (richText == null) return undefined;

  if ('children' in richText && Array.isArray(richText.children)) {
    return {
      ...richText,
      children: richText.children.map((child) => stripLinksFromElement(child)),
    };
  }
  return richText;
}

function stripLinksFromElement(element: RichText['children'][number]): RichText['children'][number] {
  if ('children' in element && Array.isArray(element.children)) {
    return {
      ...element,
      children: element.children.flatMap((child) => stripLinksFromNode(child)),
    } as RichText['children'][number];
  }
  return element;
}

function stripLinksFromNode(node: RichTextNode): RichTextNode[] {
  if ('type' in node && node.type === 'link') {
    return node.children?.flatMap((child) => stripLinksFromNode(child)) || [];
  }

  if ('children' in node && Array.isArray(node.children)) {
    return [
      {
        ...node,
        children: node.children.flatMap((child) => stripLinksFromNode(child)),
      } as RichTextNode,
    ];
  }

  return [node];
}
